export type CallType = {
  id: string
  description: string
  category: CallTypeCategory
  alertable: boolean
}

type CallTypeCategory =
  | 'Aid'
  | 'Aircraft'
  | 'Alarm'
  | 'Assist'
  | 'Explosion'
  | 'Fire'
  | 'Hazard'
  | 'Investigation'
  | 'Lockout'
  | 'Medical'
  | 'Natural Disaster'
  | 'Rescue'
  | 'Vehicle'
  | 'Wires'
  | 'Other'
  | 'Alert'
  | 'Unknown'

const callTypeCategories: CallTypeCategory[] = [
  'Aid',
  'Aircraft',
  'Alarm',
  'Assist',
  'Explosion',
  'Fire',
  'Hazard',
  'Investigation',
  'Lockout',
  'Medical',
  'Natural Disaster',
  'Rescue',
  'Vehicle',
  'Wires',
  'Other',
  'Alert',
  'Unknown'
]

export const incidentCallTypeCategories: CallTypeCategory[] = [
  'Aid',
  'Aircraft',
  'Alarm',
  'Assist',
  'Explosion',
  'Fire',
  'Hazard',
  'Investigation',
  'Lockout',
  'Medical',
  'Natural Disaster',
  'Other',
  'Rescue',
  'Vehicle',
  'Wires'
]

export const callTypeFromCode = (callTypeCode: string): CallType => {
  return (
    callTypes.find(callType => callType.id === callTypeCode) ?? {
      id: 'UNK',
      description: 'Unknown Call Type',
      category: 'Unknown',
      alertable: false
    }
  )
}

export const callTypesForCategories = (categories: CallTypeCategory[]) => {
  return callTypes.filter(callType => categories.includes(callType.category))
}

export const allCallTypeCodes = () => {
  return callTypes.map(callType => callType.id)
}

export const callTypeDescription = (code: string) => {
  const callType = callTypeFromCode(code)
  const alertSuffix = callType.alertable ? ' *' : ''
  return `${callType.description}${alertSuffix}`
}

const callTypes: CallType[] = [
  { id: 'AA', description: 'Auto Aid', category: 'Aid', alertable: true },
  { id: 'MU', description: 'Mutual Aid', category: 'Aid', alertable: true },
  { id: 'ST', description: 'Strike Team/Task Force', category: 'Aid', alertable: true },
  { id: 'AC', description: 'Aircraft Crash', category: 'Aircraft', alertable: true },
  { id: 'AE', description: 'Aircraft Emergency', category: 'Aircraft', alertable: true },
  { id: 'AES', description: 'Aircraft Emergency Standby', category: 'Aircraft', alertable: true },
  { id: 'LZ', description: 'Landing Zone', category: 'Aircraft', alertable: true },
  { id: 'AED', description: 'AED Alarm', category: 'Alarm', alertable: false },
  { id: 'OA', description: 'Alarm', category: 'Alarm', alertable: false },
  { id: 'CMA', description: 'Carbon Monoxide', category: 'Alarm', alertable: false },
  { id: 'FA', description: 'Fire Alarm', category: 'Alarm', alertable: false },
  { id: 'MA', description: 'Manual Alarm', category: 'Alarm', alertable: false },
  { id: 'SD', description: 'Smoke Detector', category: 'Alarm', alertable: false },
  { id: 'TRBL', description: 'Trouble Alarm', category: 'Alarm', alertable: false },
  { id: 'WFA', description: 'Waterflow Alarm', category: 'Alarm', alertable: false },
  { id: 'FL', description: 'Flooding', category: 'Assist', alertable: true },
  { id: 'LR', description: 'Ladder Request', category: 'Assist', alertable: false },
  { id: 'LA', description: 'Lift Assist', category: 'Assist', alertable: false },
  { id: 'PA', description: 'Police Assist', category: 'Assist', alertable: false },
  { id: 'PS', description: 'Public Service', category: 'Assist', alertable: false },
  { id: 'SH', description: 'Sheared Hydrant', category: 'Assist', alertable: false },
  { id: 'EX', description: 'Explosion', category: 'Explosion', alertable: false },
  { id: 'PE', description: 'Pipeline Emergency', category: 'Explosion', alertable: true },
  { id: 'TE', description: 'Transformer Explosion', category: 'Explosion', alertable: true },
  { id: 'AF', description: 'Appliance Fire', category: 'Fire', alertable: false },
  { id: 'CHIM', description: 'Chimney Fire', category: 'Fire', alertable: false },
  { id: 'CF', description: 'Commercial Fire', category: 'Fire', alertable: true },
  { id: 'WSF', description: 'Confirmed Structure Fire', category: 'Fire', alertable: true },
  { id: 'WVEG', description: 'Confirmed Vegetation Fire', category: 'Fire', alertable: true },
  { id: 'CB', description: 'Controlled Burn/Prescribed Fire', category: 'Fire', alertable: false },
  { id: 'ELF', description: 'Electrical Fire', category: 'Fire', alertable: false },
  { id: 'EF', description: 'Extinguished Fire', category: 'Fire', alertable: false },
  { id: 'FIRE', description: 'Fire', category: 'Fire', alertable: false },
  { id: 'FULL', description: 'Full Assignment', category: 'Fire', alertable: false },
  { id: 'IF', description: 'Illegal Fire', category: 'Fire', alertable: false },
  { id: 'MF', description: 'Marine Fire', category: 'Fire', alertable: false },
  { id: 'OF', description: 'Outside Fire', category: 'Fire', alertable: false },
  { id: 'PF', description: 'Pole Fire', category: 'Fire', alertable: true },
  { id: 'GF', description: 'Refuse/Garbage Fire', category: 'Fire', alertable: false },
  { id: 'RF', description: 'Residential Fire', category: 'Fire', alertable: true },
  { id: 'SF', description: 'Structure Fire', category: 'Fire', alertable: true },
  { id: 'TF', description: 'Tank Fire', category: 'Fire', alertable: false },
  { id: 'VEG', description: 'Vegetation Fire', category: 'Fire', alertable: true },
  { id: 'VF', description: 'Vehicle Fire', category: 'Fire', alertable: true },
  { id: 'WF', description: 'Confirmed Fire', category: 'Fire', alertable: false },
  { id: 'WCF', description: 'Working Commercial Fire', category: 'Fire', alertable: true },
  { id: 'WRF', description: 'Working Residential Fire', category: 'Fire', alertable: true },
  { id: 'BT', description: 'Bomb Threat', category: 'Hazard', alertable: false },
  { id: 'EE', description: 'Electrical Emergency', category: 'Hazard', alertable: true },
  { id: 'EM', description: 'Emergency', category: 'Hazard', alertable: false },
  { id: 'ER', description: 'Emergency Response', category: 'Hazard', alertable: false },
  { id: 'GAS', description: 'Gas Leak', category: 'Hazard', alertable: true },
  { id: 'HC', description: 'Hazardous Condition', category: 'Hazard', alertable: false },
  { id: 'HMR', description: 'Hazardous Response', category: 'Hazard', alertable: true },
  { id: 'TD', description: 'Tree Down', category: 'Hazard', alertable: false },
  { id: 'WE', description: 'Water Emergency', category: 'Hazard', alertable: true },
  { id: 'AI', description: 'Arson Investigation', category: 'Investigation', alertable: false },
  { id: 'FWI', description: 'Fireworks Investigation', category: 'Investigation', alertable: false },
  { id: 'HMI', description: 'Hazmat Investigation', category: 'Investigation', alertable: false },
  { id: 'INV', description: 'Investigation', category: 'Investigation', alertable: false },
  { id: 'OI', description: 'Odor Investigation', category: 'Investigation', alertable: false },
  { id: 'SI', description: 'Smoke Investigation', category: 'Investigation', alertable: false },
  { id: 'CL', description: 'Commercial Lockout', category: 'Lockout', alertable: false },
  { id: 'LO', description: 'Lockout', category: 'Lockout', alertable: false },
  { id: 'RL', description: 'Residential Lockout', category: 'Lockout', alertable: false },
  { id: 'VL', description: 'Vehicle Lockout', category: 'Lockout', alertable: false },
  { id: 'CP', description: 'Community Paramedicine', category: 'Medical', alertable: false },
  { id: 'IFT', description: 'Interfacility Transfer', category: 'Medical', alertable: false },
  { id: 'ME', description: 'Medical Emergency', category: 'Medical', alertable: false },
  { id: 'MCI', description: 'Multi Casualty', category: 'Medical', alertable: true },
  { id: 'EQ', description: 'Earthquake', category: 'Natural Disaster', alertable: true },
  { id: 'FLW', description: 'Flood Warning', category: 'Natural Disaster', alertable: true },
  { id: 'TOW', description: 'Tornado Warning', category: 'Natural Disaster', alertable: true },
  { id: 'TSW', description: 'Tsunami Warning', category: 'Natural Disaster', alertable: true },
  { id: 'WX', description: 'Weather Incident', category: 'Natural Disaster', alertable: false },
  { id: 'AR', description: 'Animal Rescue', category: 'Rescue', alertable: true },
  { id: 'CR', description: 'Cliff Rescue', category: 'Rescue', alertable: true },
  { id: 'CSR', description: 'Confined Space Rescue', category: 'Rescue', alertable: true },
  { id: 'ELR', description: 'Elevator Rescue', category: 'Rescue', alertable: true },
  { id: 'EER', description: 'Elevator/Escalator Rescue', category: 'Rescue', alertable: true },
  { id: 'IR', description: 'Ice Rescue', category: 'Rescue', alertable: true },
  { id: 'IA', description: 'Industrial Accident', category: 'Rescue', alertable: false },
  { id: 'RES', description: 'Rescue', category: 'Rescue', alertable: true },
  { id: 'RR', description: 'Rope Rescue', category: 'Rescue', alertable: true },
  { id: 'SC', description: 'Structural Collapse', category: 'Rescue', alertable: false },
  { id: 'TR', description: 'Technical Rescue', category: 'Rescue', alertable: true },
  { id: 'TNR', description: 'Trench Rescue', category: 'Rescue', alertable: true },
  { id: 'USAR', description: 'Urban Search and Rescue', category: 'Rescue', alertable: true },
  { id: 'VS', description: 'Vessel Sinking', category: 'Rescue', alertable: true },
  { id: 'WR', description: 'Water Rescue', category: 'Rescue', alertable: true },
  { id: 'TCP', description: 'Collision Involving Pedestrian', category: 'Vehicle', alertable: true },
  { id: 'TCS', description: 'Collision Involving Structure', category: 'Vehicle', alertable: true },
  { id: 'TCT', description: 'Collision Involving Train', category: 'Vehicle', alertable: true },
  { id: 'TCE', description: 'Expanded Traffic Collision', category: 'Vehicle', alertable: true },
  { id: 'RTE', description: 'Railroad/Train Emergency', category: 'Vehicle', alertable: true },
  { id: 'TC', description: 'Traffic Collision', category: 'Vehicle', alertable: true },
  { id: 'PLE', description: 'Powerline Emergency', category: 'Wires', alertable: true },
  { id: 'WA', description: 'Wires Arching', category: 'Wires', alertable: true },
  { id: 'WD', description: 'Wires Down', category: 'Wires', alertable: true },
  { id: 'WDA', description: 'Wires Down/Arcing', category: 'Wires', alertable: true },
  { id: 'BP', description: 'Burn Permit', category: 'Other', alertable: false },
  { id: 'CA', description: 'Community Activity', category: 'Other', alertable: false },
  { id: 'FW', description: 'Fire Watch', category: 'Other', alertable: false },
  { id: 'MC', description: 'Move-up/Cover', category: 'Other', alertable: false },
  { id: 'NO', description: 'Notification', category: 'Other', alertable: false },
  { id: 'STBY', description: 'Standby', category: 'Other', alertable: false },
  { id: 'TEST', description: 'Test', category: 'Other', alertable: false },
  { id: 'TRNG', description: 'Training', category: 'Other', alertable: false },
  { id: 'NEWS', description: 'News', category: 'Alert', alertable: false },
  { id: 'CERT', description: 'CERT', category: 'Alert', alertable: false },
  { id: 'DISASTER', description: 'Disaster', category: 'Alert', alertable: false },
  { id: 'UNK', description: 'Unknown Call Type', category: 'Unknown', alertable: false }
]
